/**
 * Game-styled controls of the port's own screens (the sign-in screen, the account button of the settings screen). The
 * SWF has no symbols for them, so they are assembled at runtime from the original symbols and the game's text style
 * (Arial, the colours of the popups), the same way the language switch and the connection overlay are:
 *  - the big 145x40 button of the popups (the green 'meet_id' of menu_friends, the beige 'proceed' of menu_question:
 *    their up / over / down states) with a label on top, as the SWF screens place theirs;
 *  - the small text button of the main screen's top bar ('init_settings' of _mainframe, 98x23, the label is part of
 *    its states) with another label;
 *  - an input field in the style of the worm name fields of the settings screen (white, black frame, Arial);
 *  - a text link (bold, underlined) and a plain label.
 * Each control has a `name`, so tests and the hover logic can find it in the display list.
 */
import { MouseEvent, Sprite, TextField, TextFormat, flashRuntime } from '@flash';

/** Text colours of the popups: body text, the dark grey of the button labels, the red of errors, the green of links. */
export const TEXT_COLOR = 0x000000;
export const LABEL_COLOR = 0x333333;
export const ERROR_COLOR = 0xff0000;
export const LINK_COLOR = 0x338000;
export const HINT_COLOR = 0x555555;

/** A text field that is not typed into: Arial, not selectable, never the target of the mouse. */
export function label(text: string, opts: { size?: number; bold?: boolean; color?: number; align?: string; width?: number; lines?: number } = {}): TextField {
  const tf = new TextField();
  const fmt = new TextFormat('Arial', opts.size ?? 12, opts.color ?? TEXT_COLOR, opts.bold ?? false);
  if (opts.align) fmt.align = opts.align;
  tf.defaultTextFormat = fmt;
  tf.selectable = false;
  tf.mouseEnabled = false;
  if (opts.width !== undefined) {
    tf.width = opts.width;
    tf.wordWrap = (opts.lines ?? 1) > 1;
    tf.multiline = (opts.lines ?? 1) > 1;
    tf.height = lineHeight(opts.size ?? 12) * (opts.lines ?? 1) + 4;
  } else tf.autoSize = 'left';
  tf.text = text;
  return tf;
}

/** Height of one line of Arial `size` in a text field (layout of the runtime, rounded up). */
function lineHeight(size: number): number {
  return Math.ceil(size * 1.15);
}

/**
 * Fits the text of a field of a fixed size: its font (from the field's own size down to `minSize`) shrinks until the
 * text takes at most `lines` lines (a field without word wrap: until it is as wide as the field).
 */
export function fitText(tf: TextField, lines = 1, minSize = 8): void {
  if (!tf.text) return;
  let size = Number(tf.defaultTextFormat.size ?? 12);
  const apply = () => {
    const f = new TextFormat();
    f.size = size;
    tf.setTextFormat(f);
  };
  apply();
  const fits = () => (tf.wordWrap ? tf.numLines <= lines : tf.textWidth <= tf.width - 4);
  while (!fits() && size > minSize) {
    size--;
    apply();
  }
}

/** Sets a text that is data, not game text (a nickname): it is shown as typed, never translated. */
export function setRawText(tf: TextField, text: string): void {
  tf.text = '';
  if (text) tf.replaceText(0, 0, text);
}

/**
 * Shrinks the font of a field's current text a point at a time (down to `minSize`) until the text fits the field:
 * all its lines in the height, or a single-line field's text in the width less `gutter` (the field's own 2 px each
 * side; more where its text must keep clear of what borders the field). For a translation longer than the original
 * in a field sized for it; the other text attributes (bold runs, colours) stay.
 */
export function shrinkToFit(tf: TextField, minSize = 9, gutter = 4): void {
  // (kept for the phone's text growth: ui/wide-popups.ts grows a field only as far as this room stays)
  if (gutter !== 4) (tf as any).$fitGutter = gutter;
  const over = () => (tf.wordWrap ? tf.maxScrollV > 1 : tf.textWidth > tf.width - gutter);
  let size = Number(tf.getTextFormat().size ?? tf.defaultTextFormat.size ?? 12);
  while (over() && size > minSize) {
    size -= 1;
    const fmt = new TextFormat();
    fmt.size = size;
    tf.setTextFormat(fmt);
  }
}

/**
 * A text field the original placed squeezed - with a horizontal scale of its own, which drew its glyphs that much
 * narrower (the shop's weapon filter, sprite 539: 0.61 x 1; tests/assets/text-proportions.test.ts finds every such
 * field) - shows its text in the glyphs' own proportions: the field keeps its box on the screen (left edge, width,
 * height), the horizontal scale becomes the vertical one, and a line wider than the box gets a smaller font (down to
 * `minSize`) instead of narrower glyphs. Idempotent: call it after every write of the field's text.
 */
export function unsqueezeText(tf: TextField, minSize = 9): void {
  const sx = tf.scaleX;
  const sy = tf.scaleY;
  if (sy > 0 && Math.abs(sx / sy - 1) > 0.01) {
    const parent = tf.parent;
    const before = parent ? tf.getBounds(parent) : null;
    const shown = tf.width;
    tf.scaleX = sy;
    tf.width = shown / sy;
    if (before && parent) tf.x += before.x - tf.getBounds(parent).x;
  }
  const over = () => tf.textWidth > tf.width / (tf.scaleX || 1) - 4;
  let size = Number(tf.getTextFormat().size ?? tf.defaultTextFormat.size ?? 12);
  while (over() && size > minSize) {
    size -= 1;
    const fmt = new TextFormat();
    fmt.size = size;
    tf.setTextFormat(fmt);
  }
}

/**
 * Sets display text holding a player's name (setRawText) so that it fits `maxWidth` (by default the field's width
 * less its gutters): a name too long is cut with "…", so the words around it (" (хост)", the closing parenthesis)
 * stay visible. Returns whether the name was cut.
 */
export function setRawTextFit(tf: TextField, text: string, name: string, maxWidth = tf.width - 4): boolean {
  setRawText(tf, text);
  const at = name ? text.indexOf(name) : -1;
  if (at < 0 || tf.textWidth <= maxWidth) return false;
  const chars = Array.from(name);
  const put = (n: number) => setRawText(tf, text.slice(0, at) + chars.slice(0, n).join('').trimEnd() + '…' + text.slice(at + name.length));
  let lo = 0;
  let hi = chars.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    put(mid);
    if (tf.textWidth <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  put(lo);
  return true;
}

/**
 * The frame of the game's popups (menu_tired's frame with its beanstalk and bones, without its texts, icon and
 * buttons), scaled by `scale` and placed so that the frame's own top-left corner lands on (frameX, frameY). The sign-in
 * screen and the delete-account dialog are built on it.
 */
export function popupFrame(fwp: any, frameX: number, frameY: number, scale: number): Sprite {
  const tired = fwp.container_interf.get_object('menu_tired');
  let frame: any = null;
  for (let i = 0; i < tired.numChildren; i++) {
    const c = tired.getChildAt(i);
    const b = c.getBounds(tired);
    if (c instanceof TextField || c === tired.refresh || c === tired.close) c.visible = false;
    else if (b.width < 40 && b.height < 40) c.visible = false; // the tiredness icon
    else if (!frame || b.width * b.height > frame.b.width * frame.b.height) frame = { c, b };
  }
  tired.mouseChildren = false;
  tired.mouseEnabled = false;
  tired.scaleX = tired.scaleY = scale;
  const fb = frame ? frame.b : { x: 188, y: 57 };
  tired.x = frameX - fb.x * scale;
  tired.y = frameY - fb.y * scale;
  return tired;
}

/** Detaches the named child of a fresh instance of `symbol` of the INTERF library (a button of an original screen). */
function symbolChild(fwp: any, symbol: string, child: string): any {
  const screen = fwp.container_interf.get_object(symbol);
  const obj = screen && screen[child];
  if (!obj) throw new Error(`controls: ${symbol} has no ${child}`);
  screen.removeChild(obj);
  obj.x = 0;
  obj.y = 0;
  return obj;
}

/** A rectangle in a control's own coordinates. */
export interface HitRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A transparent rectangle (never drawn: alpha 0) that takes the taps of the button or sprite it is the hit area of. */
function hitShape(r: HitRect, dx = 0, dy = 0): Sprite {
  const s = new Sprite();
  s.name = '$hit';
  s.graphics.beginFill(0, 0);
  s.graphics.drawRect(r.x - dx, r.y - dy, r.width, r.height);
  s.graphics.endFill();
  return s;
}

/**
 * PORT (touch): a button of the SWF (a SimpleButton inside a control, placed at bx, by) taking the taps of `r` (the
 * control's coordinates; its art's own hit area again with null): a finger's target larger than its art.
 */
function setButtonHit(button: any, r: HitRect | null, orig: { state: any }): void {
  if (orig.state === undefined) orig.state = button.hitTestState;
  button.hitTestState = r ? hitShape(r, button.x, button.y) : orig.state;
}

/** Puts `obj` so that its bounds start at (0, 0) of its parent (SWF buttons have their registration point anywhere). */
function alignTopLeft(obj: any): void {
  const b = obj.getBounds(obj);
  obj.x = -b.x;
  obj.y = -b.y;
}

/**
 * A big popup button (145x40) with a label: hover and pressed states come from the SWF button, disabled is dimmed and
 * does not react to the mouse, and a busy button shows another label (e.g. "Please wait…") while disabled.
 */
export class BigButton extends Sprite {
  readonly button: any;
  readonly caption: TextField;
  private text = '';
  private active = true;

  constructor(fwp: any, name: string, text: string, style: 'green' | 'beige' = 'green') {
    super();
    this.name = name;
    // (A timeline-placed button keeps its SWF name; this container carries the control's name.)
    this.button = style === 'green' ? symbolChild(fwp, 'menu_friends', 'meet_id') : symbolChild(fwp, 'menu_question', 'proceed');
    alignTopLeft(this.button);
    this.addChild(this.button);
    const b = this.button.getBounds(this);
    this.caption = label('', { size: 12, bold: true, color: LABEL_COLOR, align: 'center', width: b.width, lines: 2 });
    this.addChild(this.caption);
    this.setText(text);
  }

  /** The label (the source text; the field shows its translation). */
  setText(text: string): void {
    this.text = text;
    this.caption.text = text;
    const b = this.button.getBounds(this);
    this.caption.y = Math.round(b.y + (b.height - this.caption.textHeight) / 2 - 2);
  }

  get label(): string {
    return this.text;
  }

  private readonly hitOrig: { state: any } = { state: undefined };

  /** The taps of `r` (this control's coordinates) reach the button, beyond its art (null: its art's area). */
  setHitRect(r: HitRect | null): void {
    setButtonHit(this.button, r, this.hitOrig);
  }

  get enabled(): boolean {
    return this.active;
  }

  set enabled(on: boolean) {
    this.active = on;
    this.mouseEnabled = on;
    this.mouseChildren = on;
    this.button.enabled = on;
    this.alpha = on ? 1 : 0.55;
  }
}

/**
 * The small buttons' caption: the light green of the main map's labels on the dark plate (the HUD's `labels`,
 * src/game/ui/wide-screen.ts; the SWF's own dark green on it read as a disabled button), 12 px at most so that the
 * plate (a pill 82 x 16 px in a 98 x 23 box) keeps a margin round it, above and below as at both ends.
 */
export const SMALL_CAPTION_COLOR = 0xd4f0a0;
const SMALL_CAPTION_SIZE = 12;
/** The pill of init_settings' states (shape 1352: 81.9 x 16.2, centred on the button's registration point). */
const SMALL_PILL = { width: 81.9, centreY: 0.05 };

/**
 * A small text button of the main screen's top bar (98x23, the label is drawn in each of its states); a label wider
 * than the button gets a smaller font.
 */
export class SmallButton extends Sprite {
  readonly button: any;
  private text = '';
  private readonly hitOrig: { state: any } = { state: undefined };

  constructor(fwp: any, name: string, text: string) {
    super();
    this.name = name;
    this.button = symbolChild(fwp, '_mainframe', 'init_settings');
    this.button.x = this.button.y = 0;
    this.addChild(this.button);
    this.setText(text);
  }

  setText(text: string): void {
    this.text = text;
    const b = this.button;
    // (The states' art as the SWF places it: the button at its registration point while the labels are set.)
    b.x = b.y = 0;
    for (const state of [b.upState, b.overState, b.downState, b.hitTestState]) {
      const tf = findText(state);
      if (!tf) continue;
      const fmt = tf.defaultTextFormat;
      fmt.size = SMALL_CAPTION_SIZE;
      fmt.color = SMALL_CAPTION_COLOR;
      tf.defaultTextFormat = fmt;
      tf.text = text;
      // The original labels are one word; a longer translation shrinks until it fits the pill, with a margin at both
      // ends (at full size "sign out" filled the plate edge to edge and its "g" hung over the bottom border).
      while (tf.textWidth > SMALL_PILL.width - 12 && fmt.size > 9) {
        fmt.size -= 1;
        tf.defaultTextFormat = fmt;
        tf.text = text;
      }
      // Its line in the pill's middle (the field's 2 px gutter above the text).
      tf.y = Math.round((SMALL_PILL.centreY - tf.textHeight / 2 - 2) * 10) / 10;
    }
    alignTopLeft(this.button);
  }

  get label(): string {
    return this.text;
  }

  /** The taps of `r` (this control's coordinates) reach the button, beyond its art (null: its art's area). */
  setHitRect(r: HitRect | null): void {
    setButtonHit(this.button, r, this.hitOrig);
  }

  set enabled(on: boolean) {
    this.mouseEnabled = on;
    this.mouseChildren = on;
    this.button.enabled = on;
    this.alpha = on ? 1 : 0.55;
  }
}

function findText(o: any): TextField | null {
  if (!o) return null;
  if (o instanceof TextField) return o;
  const n = typeof o.numChildren === 'number' ? o.numChildren : 0;
  for (let i = 0; i < n; i++) {
    const hit = findText(o.getChildAt(i));
    if (hit) return hit;
  }
  return null;
}

/** An input field in the style of the settings screen's worm name fields. */
export function inputField(name: string, opts: { width: number; maxChars: number; restrict?: string | null; password?: boolean }): TextField {
  const tf = new TextField();
  const fmt = new TextFormat('Arial', 13, TEXT_COLOR, false);
  fmt.leftMargin = 3;
  fmt.rightMargin = 3;
  tf.defaultTextFormat = fmt;
  tf.name = name;
  tf.type = 'input';
  tf.border = true;
  tf.borderColor = 0x000000;
  tf.background = true;
  tf.backgroundColor = 0xffffff;
  tf.multiline = false;
  tf.wordWrap = false;
  tf.width = opts.width;
  tf.height = 22;
  tf.maxChars = opts.maxChars;
  tf.restrict = opts.restrict ?? null;
  tf.displayAsPassword = !!opts.password;
  return tf;
}

/** A clickable text (bold, underlined, the green of the language switch) with the hand cursor. */
export class TextLink extends Sprite {
  readonly field: TextField;

  constructor(name: string, text: string, onClick: () => void) {
    super();
    this.name = name;
    this.field = label('', { size: 12, bold: true, color: LINK_COLOR });
    const fmt = this.field.defaultTextFormat;
    fmt.underline = true;
    this.field.defaultTextFormat = fmt;
    this.field.text = text;
    this.addChild(this.field);
    this.buttonMode = true;
    this.useHandCursor = true;
    this.mouseChildren = false;
    this.addEventListener(MouseEvent.CLICK, () => onClick());
  }

  setText(text: string): void {
    this.field.text = text;
  }

  /**
   * The taps of `r` (this control's coordinates) reach the link beyond its text (null: its text only): a transparent
   * rectangle under the text (the link keeps its children from the mouse, so it is the target).
   */
  setHitRect(r: HitRect | null): void {
    const old = this.getChildByName('$hit');
    if (old) this.removeChild(old);
    if (r) this.addChildAt(hitShape(r), 0);
  }
}

/** A line of an article that starts a paragraph of its own (a list item: "- ...", "1) ...", "• ..."). */
const LIST_ITEM = /^\s*(?:[-–—•*]|\d+[).])\s/;

/**
 * The Wormpedia's articles (menu_wormpedia, the frames of `articles`: one text field of 30-40 lines each) in every
 * language: the original broke their lines by hand for its Russian text (each line a paragraph of the field, no word
 * wrap), the translations keep those breaks, and their longer lines ran past the panel's right edge and under the
 * beanstalk's leaves (uk "Щоправда, у вас виникає та|"), English broke mid-sentence. The article's lines are joined
 * again into its paragraphs (a blank line, a list item or a line after a colon starts one), word-wrapped in a width
 * that ends STALK_GAP px before the leaves (and inside the frame), a point smaller where the text would not fit the
 * field's height (down to 9 px), else the field scrolls (the mouse wheel; scrollV). Each field once (the timeline
 * builds a frame's field anew when the frame is shown again).
 */
export function reflowArticle(articles: any, screen: any): void {
  if (!articles || typeof articles.numChildren !== 'number' || !screen) return;
  let stalk: any = null;
  let frame: any = null;
  for (let i = 0; i < screen.numChildren; i++) {
    const c = screen.getChildAt(i);
    if (c.$charId === BEANSTALK && c.visible) stalk = c;
    else if (!frame && !(c instanceof TextField) && c.visible && typeof c.getBounds === 'function') {
      const b = c.getBounds(screen);
      if (b.width > 600 && b.height > 400) frame = b;
    }
  }
  for (let i = 0; i < articles.numChildren; i++) {
    const tf = articles.getChildAt(i);
    if (!(tf instanceof TextField) || tf.type === 'input' || (tf as any).$reflowed === tf.htmlText) continue;
    const html = String(tf.htmlText ?? '');
    const paras = html.split(/<\/P>/i).filter((p) => p.trim() !== '');
    if (paras.length < 4) continue;
    const plain = (p: string) => p.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&[a-z]+;/g, 'x');
    let out = '';
    let prev = '';
    for (const p of paras) {
      const text = plain(p);
      const join = prev.trim() !== '' && text.trim() !== '' && !LIST_ITEM.test(text) && !/[:：]\s*$/.test(prev);
      if (join) out += (/\s$/.test(prev) || /^\s/.test(text) ? '' : ' ') + p.replace(/^\s*<P[^>]*>/i, '');
      else out += (out ? '</P>' : '') + p;
      prev = text;
    }
    out += '</P>';
    const b = tf.getBounds(screen);
    const k = b.width / Math.max(1, tf.width);
    let right = b.x + b.width;
    if (frame) right = Math.min(right, frame.x + frame.width - 14);
    if (stalk) {
      const sb = stalk.getBounds(screen);
      if (sb.y < b.y + b.height && b.y < sb.y + sb.height) right = Math.min(right, sb.x - STALK_GAP);
    }
    const height = tf.height;
    tf.wordWrap = true;
    tf.width = Math.max(100, (right - b.x) / Math.max(0.01, k));
    // (the text is already in the player's language: not looked up in the catalogue again)
    const runtime = flashRuntime();
    const localize = runtime.localize;
    runtime.localize = null;
    try {
      tf.htmlText = out;
    } finally {
      runtime.localize = localize;
    }
    tf.height = height;
    const size = tf.getTextFormat().size;
    if (typeof size === 'number') {
      for (let px = size; tf.maxScrollV > 1 && px > 9; ) {
        const f = new TextFormat();
        f.size = --px;
        tf.setTextFormat(f);
      }
    }
    (tf as any).$reflowed = tf.htmlText;
  }
}

/** The beanstalk decoration of the popups' frames (sprite 65: its leaves hang over the frame's right edge). */
export const BEANSTALK = 65;
/** Room between a text and the beanstalk's leaves (px). */
export const STALK_GAP = 4;

/**
 * Keeps the texts of a popup clear of its frame's beanstalk (a translation longer than the original's text ran under
 * its leaves: the Papa John shop's description): a word-wrapped text field beside the stalk whose lines reach under
 * its leaves is narrowed to end STALK_GAP px before them, if its text still takes no more lines than the field holds
 * (or does so a point smaller; else it stays as it was). The field's original width is kept, so a shorter text later
 * gets it back.
 */
export function clearOfBeanstalk(screen: any): void {
  if (!screen || typeof screen.numChildren !== 'number') return;
  let stalk: any = null;
  for (let i = 0; i < screen.numChildren; i++) if (screen.getChildAt(i).$charId === BEANSTALK && screen.getChildAt(i).visible) stalk = screen.getChildAt(i);
  if (!stalk) return;
  const s = stalk.getBounds(screen);
  for (let i = 0; i < screen.numChildren; i++) {
    const tf = screen.getChildAt(i);
    if (!(tf instanceof TextField) || !tf.wordWrap || !tf.visible || tf.type === 'input') continue;
    const w0: number = (tf as any).$stalkWidth ?? tf.width;
    const b = tf.getBounds(screen);
    const right = b.x + w0 * (b.width / Math.max(1, tf.width));
    const limit = s.x - STALK_GAP;
    if (!(b.y < s.y + s.height && s.y < b.y + b.height) || right <= limit || b.x >= limit) continue;
    (tf as any).$stalkWidth = w0;
    if (tf.width !== w0) tf.width = w0;
    // (the text's own lines: where they end short of the leaves at its full width, nothing to do)
    let reach = 0;
    for (let l = 0; l < tf.numLines; l++) reach = Math.max(reach, tf.getLineMetrics(l).x + tf.getLineMetrics(l).width);
    if (b.x + 2 + reach <= limit) continue;
    tf.width = Math.max(40, limit - b.x);
    if (tf.maxScrollV <= 1) continue;
    // One line more than the field holds: a point smaller than the game's size (a text of one size), else as it was.
    // (A text this made smaller and the game did not write anew is not made smaller again.)
    const size = tf.getTextFormat().size;
    const base: number | undefined = (tf as any).$stalkBase === (typeof size === 'number' ? size + 1 : NaN) ? size + 1 : size;
    if (typeof size === 'number' && typeof base === 'number' && base > 10) {
      const f = new TextFormat();
      f.size = base - 1;
      tf.setTextFormat(f);
      (tf as any).$stalkBase = base;
      if (tf.maxScrollV <= 1) continue;
      f.size = base;
      tf.setTextFormat(f);
    }
    tf.width = w0;
  }
}
